import { useState, useEffect } from 'react'
import Card, { CardHeader, CardContent } from '../ui/Card'
import Button from '../ui/Button'
import Listbox from '../ui/Listbox'
import LoadingState from '../ui/LoadingState'
import { reporteService } from '../../services/reporteService'
import Chart from 'react-apexcharts'
import {
  createArtemisAxisOptions,
  createArtemisBaseOptions,
  getArtemisPalette,
} from '../../utils/artemisApexTheme'
import { TrendingUp, RefreshCw, AlertCircle, Download, TrendingDown } from 'lucide-react'

export default function Tendencias({ idClinica = null }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [periodo, setPeriodo] = useState('12')

  useEffect(() => {
    // Solo hacer fetch si idClinica está definido (incluyendo 0) o si es null explícitamente después de que el usuario se haya cargado
    if (idClinica !== undefined) {
      fetchData()
    }
  }, [periodo, idClinica])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = { periodo }
      // IMPORTANTE: Solo agregar id_clinica si es un número válido mayor que 0 (no null, no undefined, no 0)
      // null y undefined significan "dejar que el backend lo resuelva automáticamente"
      if (idClinica != null && idClinica !== undefined && !isNaN(Number(idClinica)) && Number(idClinica) > 0) {
        params.id_clinica = Number(idClinica)
      }
      
      const response = await reporteService.getTendencias(params)
      if (response.success) {
        setData(response.data)
      } else {
        setError(response.error || 'Error al cargar tendencias')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <LoadingState message="Cargando tendencias..." size="lg" />
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <Button onClick={fetchData} variant="secondary" className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return null
  }

  const palette = getArtemisPalette()
  const axisOptions = createArtemisAxisOptions()

  // Configuración gráfico tendencia citas
  const areaBaseOptions = createArtemisBaseOptions({
    type: 'area',
    height: 400,
    colors: [palette[1], palette[2], palette[0]],
  })
  const chartTendenciaOptions = {
    ...areaBaseOptions,
    xaxis: {
      ...axisOptions,
      categories: data.tendenciaCitas.map(item => item.mesCorto),
    },
    yaxis: {
      ...axisOptions,
      tickAmount: 5,
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 0.6,
        opacityFrom: 0.4,
        opacityTo: 0.05,
      }
    },
    markers: {
      size: 3,
      hover: { size: 5 },
    },
    grid: {
      ...areaBaseOptions.grid,
      padding: { top: 6, right: 10, left: 0, bottom: 0 },
    },
    legend: {
      ...areaBaseOptions.legend,
      position: 'top',
    },
  }

  const chartTendenciaSeries = [
    {
      name: 'Citas',
      data: data.tendenciaCitas.map(item => item.citas)
    },
    {
      name: 'Fichas',
      data: data.tendenciaCitas.map(item => item.fichas)
    },
    {
      name: 'Mascotas',
      data: data.tendenciaCitas.map(item => item.mascotas)
    }
  ]

  // Configuración gráfico especies tratadas
  const barBaseOptions = createArtemisBaseOptions({
    type: 'bar',
    height: 350,
    colors: [palette[0]],
  })
  const chartEspeciesOptions = {
    ...barBaseOptions,
    xaxis: {
      ...axisOptions,
      categories: data.especiesTratadas.map(item => item.especie),
    },
    yaxis: {
      ...axisOptions,
      tickAmount: 5,
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: '54%',
        horizontal: false,
      }
    },
    grid: {
      ...barBaseOptions.grid,
      padding: { top: 4, right: 10, left: 0, bottom: 0 },
    },
  }

  const chartEspeciesSeries = [{
    name: 'Tratamientos',
    data: data.especiesTratadas.map(item => item.cantidad)
  }]

  // Configuración gráfico tasa de completitud
  const lineBaseOptions = createArtemisBaseOptions({
    type: 'line',
    height: 350,
    colors: [palette[1]],
  })
  const chartTasaOptions = {
    ...lineBaseOptions,
    xaxis: {
      ...axisOptions,
      categories: data.tasaCompletitud.map(item => item.mesCorto),
    },
    yaxis: {
      ...axisOptions,
      labels: {
        ...axisOptions.labels,
        formatter: (val) => `${val}%`
      },
      max: 100,
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    markers: {
      size: 4,
      hover: { size: 6 },
    },
    grid: {
      ...lineBaseOptions.grid,
      padding: { top: 8, right: 10, left: 0, bottom: 0 },
    },
  }

  const chartTasaSeries = [{
    name: 'Tasa de Completitud',
    data: data.tasaCompletitud.map(item => item.tasa)
  }]

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filtros</h3>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Listbox
              label="Período (meses)"
              options={[
                { value: '6', label: 'Últimos 6 meses' },
                { value: '12', label: 'Últimos 12 meses' },
                { value: '24', label: 'Últimos 24 meses' },
              ]}
              value={periodo}
              onChange={setPeriodo}
            />
            <div className="flex items-end">
              <Button onClick={fetchData} variant="secondary" className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Variación */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Citas Mes Actual</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{data.mesActual}</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <TrendingUp className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Citas Mes Anterior</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{data.mesAnterior}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Variación</p>
                <div className="flex items-center space-x-2 mt-2">
                  {data.variacionCitas >= 0 ? (
                    <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
                  ) : (
                    <TrendingDown className="w-6 h-6 text-red-600 dark:text-red-400" />
                  )}
                  <p className={`text-3xl font-bold ${data.variacionCitas >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {Math.abs(data.variacionCitas)}%
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tendencia de Actividad</h3>
        </CardHeader>
        <CardContent>
          <Chart
            options={chartTendenciaOptions}
            series={chartTendenciaSeries}
            type="area"
            height={400}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Especies Más Tratadas</h3>
          </CardHeader>
          <CardContent>
            <Chart
              options={chartEspeciesOptions}
              series={chartEspeciesSeries}
              type="bar"
              height={350}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tasa de Completitud de Citas</h3>
          </CardHeader>
          <CardContent>
            <Chart
              options={chartTasaOptions}
              series={chartTasaSeries}
              type="line"
              height={350}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

